import React, { SyntheticEvent } from 'react';
import Box from '@mui/material/Box';
import MuiTabs from '@mui/material/Tabs';
import MuiTab from '@mui/material/Tab';
import { SxProps, Theme } from '@mui/material';

export interface TabItem {
  label: string;
  value?: string;
}

export interface TabsProps {
  tabs: TabItem[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  className?: string;
  tabClassName?: string;
  activeTabClassName?: string;
  inactiveTabClassName?: string;
  sx?: SxProps<Theme>;
}

export const Tabs = ({
  tabs,
  activeTab,
  onTabChange,
  className = '',
  sx,
}: TabsProps) => {
  const handleChange = (_event: SyntheticEvent, newValue: string) => {
    onTabChange(newValue);
  };

  return (
    <Box
      className={className}
      sx={{
        display: { xs: 'block', sm: 'inline-flex' },
        width: { xs: '100%', sm: 'auto' },
        p: 0.5,
        borderRadius: '16px',
        bgcolor: 'rgba(241, 245, 249, 0.85)',
        backdropFilter: 'blur(8px)',
        border: '1px solid',
        borderColor: 'rgba(226, 232, 240, 0.8)',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
        ...sx,
      }}
    >
      <MuiTabs
        value={activeTab}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 36,
          '& .MuiTabs-indicator': {
            display: 'none',
          },
          '& .MuiTabs-flexContainer': {
            gap: 0.75,
          },
        }}
      >
        {tabs.map(({ label, value }) => {
          const tabValue = value ?? label;
          const isActive = activeTab === tabValue;

          return (
            <MuiTab
              key={label}
              label={label}
              value={tabValue}
              disableRipple
              sx={{
                minHeight: { xs: 32, sm: 36 },
                minWidth: 'auto',
                px: { xs: 2, sm: 2.5 },
                py: 0.75,
                fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                fontWeight: isActive ? 700 : 500,
                textTransform: 'none',
                borderRadius: '12px',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                color: isActive ? 'text.primary' : 'text.secondary',
                bgcolor: isActive ? '#ffffff' : 'transparent',
                boxShadow: isActive
                  ? '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)'
                  : 'none',
                border: isActive ? '1px solid rgba(0, 0, 0, 0.04)' : '1px solid transparent',
                '&:hover': {
                  color: 'text.primary',
                  bgcolor: isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.45)',
                },
                '&.Mui-selected': {
                  color: 'text.primary',
                },
              }}
            />
          );
        })}
      </MuiTabs>
    </Box>
  );
};

export default Tabs;
